import React, { useCallback, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DollarSign } from "lucide-react";
import CustomDatePicker from "../../common/CustomDatePicker";
import { useModalStyles } from "../../common/useModalStyles";
import { useForm } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import { Cancel, EditOutlined, ErrorOutlineRounded } from "@mui/icons-material";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import Button from "../../common/Button";
import CustomTable from "../../common/CustomTable";
import { ProductItemInfo, ProductPurchasePriceInfo } from "../../../redux/slice/product/createProductSlice";
import { FieldLabel } from "../../../utils/Utility";

interface ProductItemsCogsPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: (items: ProductPurchasePriceInfo[]) => void;
  productId: number;
  productItemsList: ProductItemInfo[];
}

type Column = {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
};

const showInlineErrorMinLength = 60;

const ProductItemsCogsPopup: React.FC<ProductItemsCogsPopupProps> = ({
  open,
  onClose,
  onSave,
  productId,
  productItemsList,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    inputFieldSx,
    datepickerWrapSx,
    primaryBtnSx,
    cancelBtnSx,
    isDark,
  } = useModalStyles();

  const modalTableSx = {
    width: "100%",
    border: `1px solid ${tokens.border}`,
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : tokens.cardBg,
    "& > div": { mt: 0 },
    "& .MuiTableContainer-root": { borderRadius: 0 },
    "& .MuiTable-root": { background: "transparent !important" },
    "& .MuiTableCell-head": {
      background: `${isDark ? "rgba(255,255,255,0.03)" : tokens.cardBg} !important`,
      borderBottom: `1px solid ${tokens.border}`,
    },
    "& .MuiTableCell-head .MuiTypography-root": {
      color: `${tokens.textSecondary} !important`,
      fontWeight: 600,
      fontSize: "0.78rem",
    },
    "& .MuiTableCell-body": {
      borderBottom: `1px solid ${tokens.border}`,
      color: tokens.textPrimary,
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  };

  const { control, handleSubmit, reset, getValues, formState } = useForm<any>();
  const { errors } = formState;

  const validateAmount = (value: string, message: string) => {
    const val: number = value !== "" ? parseFloat(value) : 0;
    if (val === 0 || val === 0.0) {
      return message;
    }
  };

  const renderCurrencyField = (controllerName: string, rules?: object) => (
    <Box sx={inputFieldSx()}>
      <CustomCurrencyField
        control={control}
        controllerName={controllerName}
        label=""
        size="small"
        showErrorMinLength={showInlineErrorMinLength}
        rules={rules}
        sx={{
          "& .MuiInputAdornment-root": { color: tokens.textSecondary },
        }}
      />
    </Box>
  );

  const errorMessages = useMemo(() => {
    const errorMsgs: JSX.Element[] = [];
    if (errors) {
      const objKeys: string[] = Object.keys(errors);
      let errMsg: string | undefined;
      if (objKeys && objKeys.length > 0) {
        objKeys.forEach((key) => {
          // @ts-ignore
          errMsg = errors[key]?.message;
          if (errMsg && errMsg.length >= showInlineErrorMinLength) {
            errorMsgs.push(
              <Box key={key} sx={{ display: "flex", justifyContent: "flex-start", color: "error.main" }}>
                <Box sx={{ pr: 0.5 }}>
                  <ErrorOutlineRounded />
                </Box>
                <Box>{errMsg}</Box>
              </Box>
            );
          }
        });
      }
    }
    return errorMsgs;
  }, [errors]);

  const columns: Column[] = useMemo(() => [
    { id: "sku", label: "SKU", minWidth: 120 },
    { id: "purchasePrice", label: "Purchase Price", minWidth: 140, align: "center" },
  ], []);

  const productItemCogsRows = useMemo(() => {
    return (productItemsList || []).map((row, index) => ({
      ...row,
      purchasePrice: renderCurrencyField(`purchasePrice${row.sku}-${index}`, {
        required: "Purchase Price is required.",
        validate: {
          validateAmount: (value: string) => validateAmount(value, "Enter a valid Purchase Price."),
        },
      }),
    }));
  }, [productItemsList, control, tokens.textSecondary]);

  useEffect(() => {
    if (open) {
      reset({ effectiveDate: null, inactiveDate: null });
    }
  }, [open, reset]);

  const validateEffectiveDate = useCallback(
    (value: string | number | Date | Dayjs | null | undefined) => {
      if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
        return "Invalid date format.";
      }
      return true;
    },
    []
  );

  const validateOptionalInactiveDate = useCallback(
    (value: string | number | Date | Dayjs | null | undefined) => {
      if (
        dayjs(value, "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isAfter(dayjs(value, "MM/DD/YYYY", true))
      ) {
        return "The inactive date should be the same as or after the effective date.";
      }
      return true;
    },
    [getValues]
  );

  const onSubmit = useCallback(() => {
    const data = getValues();
    const effectiveDate = dayjs(data.effectiveDate).format("MM/DD/YYYY");
    const inactiveDate = data.inactiveDate ? dayjs(data.inactiveDate).format("MM/DD/YYYY") : null;
    const payload: ProductPurchasePriceInfo[] = (productItemsList || []).map((item, index) => ({
      productPurchasePriceId: 0,
      productId,
      productItemId: item.productItemId,
      sku: item.sku,
      effectiveDate,
      inactiveDate,
      purchasePrice: data[`purchasePrice${item.sku}-${index}`],
    }));
    onSave(payload);
  }, [getValues, onSave, productId, productItemsList]);

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-describedby="product-items-cogs-dialog"
      onClick={(event) => event.stopPropagation()}
      PaperProps={{ sx: paperSx(isMobile) }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.5)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <DollarSign size={22} color={tokens.accent} strokeWidth={1.8} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              Add Item COGs
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: tokens.textSecondary,
                mt: 0.15,
              }}
            >
              Enter COGs details and set purchase prices for each product item.
            </Typography>
          </Box>

          <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={{ ...bodySx, minHeight: 0, overflowY: "auto" }}>
          {errorMessages.length > 0 && <Box sx={{ pb: 1 }}>{errorMessages}</Box>}

          <Grid container spacing={2}>
            <Grid item md={4} xs={12}>
              <FieldLabel label="Effective Date" required tokens={tokens} />
              <Box sx={datepickerWrapSx(!!errors.effectiveDate)}>
                <CustomDatePicker
                  label=""
                  openTo="day"
                  controllerName="effectiveDate"
                  control={control}
                  size="small"
                  showErrorMinLength={showInlineErrorMinLength}
                  format="MM/DD/YYYY"
                  rules={{
                    required: "Effective Date is required.",
                    validate: {
                      isValidDate: (value: string | number | Date | Dayjs | null | undefined) =>
                        validateEffectiveDate(value),
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item md={4} xs={12}>
              <FieldLabel label="Inactive Date" tokens={tokens} />
              <Box sx={datepickerWrapSx(!!errors.inactiveDate)}>
                <CustomDatePicker
                  label=""
                  openTo="day"
                  controllerName="inactiveDate"
                  control={control}
                  size="small"
                  showErrorMinLength={showInlineErrorMinLength}
                  format="MM/DD/YYYY"
                  rules={{
                    validate: {
                      isValidDate: (value: string | number | Date | Dayjs | null | undefined) =>
                        validateOptionalInactiveDate(value),
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 1 }}>
            <FieldLabel label="Product Item COGs" tokens={tokens} />
            <CustomTable
              columns={columns}
              rows={productItemCogsRows}
              disablePagination
              emptyText="No Product Item(s) Found"
              sx={modalTableSx}
            />
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              startIcon={<EditOutlined sx={{ fontSize: "1rem !important" }} />}
              sx={primaryBtnSx}
            >
              Add COGs Item
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default ProductItemsCogsPopup;
